import React from 'react';
import { homepageSectionImagePlaceholderStretchClassName } from '../constants/homepageImagePlaceholder';
import {
  homepageCategoryStatBoxClassName,
  homepageDatasetRequestLinkClassName,
  homepageElevatedPanelClassName,
  homepageSectionContainerClassName,
  homepageSectionPaddingYClassName,
  sectionHeadingAccentCenterClassName,
  sectionHeadingAccentClassName,
} from '../constants/homepageUi';
import { HomepageImageGlowWrap } from './HomepageImageGlow';

const realWorldBlockImageClassName =
  'w-full h-auto max-w-full rounded-2xl shadow-[0_8px_48px_rgba(0,0,0,0.35)]';

type BlockProof = {
  tasksValue: string;
  subValue: string;
  captureValue: string;
  middleColumnLabel?: 'languages';
};

type Block = {
  layout: 'image-left' | 'image-right';
  title: string;
  lead: string;
  bullets: [string, string, string];
  designed: [string, string, string];
  proof: BlockProof;
  imageSrc?: string;
  imageAlt?: string;
};

type StatColumn = {
  value: string;
  label: string;
};

const STAT_COLUMNS: StatColumn[] = [
  { value: '1,255+', label: 'TASKS' },
  { value: '7+', label: 'Categories' },
  { value: '27+', label: 'SUB-CATEGORIES' },
  { value: '10+', label: 'LANGUAGES' },
  { value: 'Photo + Video', label: 'Type' },
];

const BLOCKS: Block[] = [
  {
    layout: 'image-right',
    title: 'Household & Kitchen',
    lead: 'Real-world household environments for training spatial and contextual AI models',
    bullets: [
      'Messy, cluttered, and lived-in environments — not staged datasets',
      'Capture real lighting, object placement, and human interaction patterns',
      'Built for scene understanding, robotics navigation, and home AI systems',
    ],
    designed: [
      'Object detection in real environments',
      'Scene understanding under imperfect conditions',
      'Automation in non-controlled spaces',
    ],
    proof: { tasksValue: '100+', subValue: '10+', captureValue: 'Photo + Video' },
    imageSrc: '/assets/household-home.png',
    imageAlt: 'Household and kitchen environments for spatial and contextual AI training data',
  },
  {
    layout: 'image-left',
    title: 'Handwriting & Multilingual Text',
    lead: 'High-variance handwritten data across multiple languages and formats',
    bullets: [
      'Real user-generated writing — not synthetic fonts or clean samples',
      'Includes variation in spacing, pressure, alignment, and tools',
      'Supports multilingual OCR, document AI, and recognition systems',
    ],
    designed: [
      'OCR accuracy across handwriting styles',
      'Multilingual text recognition',
      'Document parsing in real-world conditions',
    ],
    proof: { tasksValue: '1000+', subValue: '10+', captureValue: 'Photo', middleColumnLabel: 'languages' },
    imageSrc: '/assets/handwriting-home.png',
    imageAlt: 'Handwriting and multilingual text samples for OCR and document AI training data',
  },
  {
    layout: 'image-right',
    title: 'Food Condition & Quality',
    lead: 'Condition-based food datasets for detection, classification, and quality models',
    bullets: [
      'Clear progression from fresh → spoiled → moldy across real environments',
      'Includes packaging, ingredients, expiration labels, and prep states',
      'Captured in natural conditions with real lighting and imperfections',
    ],
    designed: [
      'Food quality detection systems',
      'Inventory and expiration tracking',
      'Retail and smart kitchen automation',
    ],
    proof: { tasksValue: '250+', subValue: '7', captureValue: 'Photo + Video' },
    imageSrc: '/assets/food-condition-home.png',
    imageAlt: 'Food condition and quality data for detection and classification models',
  },
];

// --- TOP ROW ONLY (larger, separate system) ---
/** items-end: every column’s block sits on the same bottom edge so all labels share one baseline (TYPE matches TASKS, etc.). */
const topStatGridClass =
  'grid w-full grid-cols-1 divide-y divide-[rgba(0,227,255,0.28)] sm:min-w-[720px] sm:grid-cols-5 sm:items-end sm:divide-y-0 sm:gap-x-5 md:min-w-0 md:gap-x-7 lg:gap-x-9 xl:gap-x-10';

const topStatCellClass =
  'flex min-w-0 flex-col items-center gap-1 px-2 py-3 sm:px-2 sm:py-0 sm:first:pl-3 lg:px-2.5 lg:first:pl-4';

const topStatNumericClass =
  'text-center text-3xl font-bold leading-none tracking-tight text-[#00e3ff] tabular-nums sm:text-4xl lg:text-5xl';

/** Same type scale as topStatNumericClass; flex so trailing + centers to digit cap height. */
const topStatNumericWithPlusRowClass =
  'inline-flex items-center justify-center gap-0 text-3xl font-bold leading-none tracking-tight text-[#00e3ff] tabular-nums sm:text-4xl lg:text-5xl';

const topStatLabelClass =
  'm-0 whitespace-nowrap text-center text-[9px] font-semibold uppercase leading-none tracking-[0.06em] text-white/60 sm:text-[10px] sm:tracking-[0.08em] lg:text-[11px] lg:tracking-[0.09em]';

/**
 * Two lines + gap-0.5 (2px) total height matches one line of topStatNumericClass (3xl / 4xl / 5xl at leading-none).
 * Math: 2×line + 0.125rem = numeric font-size at each breakpoint.
 */
const topStatPhotoVideoLineClass =
  'block text-center font-bold leading-none tracking-tight text-[#00e3ff] text-[0.875rem] sm:text-[1.0625rem] lg:text-[1.4375rem]';

const topStatPhotoVideoStackClass =
  'flex flex-col items-center gap-0.5 leading-none [&>span]:leading-none';

const topStatPlusClass = 'shrink-0 self-center text-[0.5em] font-bold leading-none tabular-nums';

function TopStatValue({ value }: { value: string }) {
  if (value === 'Photo + Video') {
    return (
      <div className={topStatPhotoVideoStackClass}>
        <span className={topStatPhotoVideoLineClass}>Photo +</span>
        <span className={topStatPhotoVideoLineClass}>Video</span>
      </div>
    );
  }
  if (value.endsWith('+')) {
    return (
      <span className={topStatNumericWithPlusRowClass}>
        <span className="tabular-nums">{value.slice(0, -1)}</span>
        <span className={topStatPlusClass}>+</span>
      </span>
    );
  }
  return <span className={topStatNumericClass}>{value}</span>;
}

function TopStatCell({ column }: { column: StatColumn }) {
  return (
    <div className={topStatCellClass}>
      <TopStatValue value={column.value} />
      <span className={topStatLabelClass}>{column.label}</span>
    </div>
  );
}

// --- LOWER 3 BOXES: one system — mobile = stacked; sm+ = 2-row grid so stat→label gap is identical in every column. ---
/** Tight, identical space between stat and label (1px). */
const lowerStatValueLabelGapClass = 'gap-px';

/** Vertical dividers: 6 grid children in row order v0,v1,v2,l0,l1,l2 — clear border on cols 1–2 only. */
const lowerStatDesktopColDividerClass =
  'sm:border-r sm:border-[rgba(0,227,255,0.28)] sm:[&:nth-child(3n)]:border-r-0';

const lowerStatBarMobileClass = 'divide-y divide-[rgba(0,227,255,0.28)] sm:hidden';

const lowerStatBarDesktopClass =
  'hidden sm:grid sm:grid-cols-3 sm:grid-rows-[auto_auto] sm:gap-y-px sm:px-4 sm:py-2.5';

const lowerStatMobileSegmentClass = `flex flex-col items-center px-3 py-2.5 ${lowerStatValueLabelGapClass}`;

const lowerStatDesktopValueCellClass = `flex items-end justify-center sm:min-h-0 sm:px-2.5 ${lowerStatDesktopColDividerClass}`;

const lowerStatDesktopLabelCellClass = `flex justify-center sm:px-2.5 ${lowerStatDesktopColDividerClass}`;

const lowerStatNumericClass =
  'text-center text-xl font-bold leading-none tracking-tight text-white tabular-nums sm:text-2xl';

/** Single-line capture (“Photo”) — same type scale as numerics (not oversized vs. numbers). */
const lowerStatCaptureSingleClass =
  'text-center text-xl font-bold leading-none tracking-tight text-white sm:text-2xl';

/** Stacked “Photo +” / “Video” — 75% of prior lg/xl scale; value slot + shared label row keep CAPTURE aligned. */
const lowerStatCaptureLineClass =
  'block text-center font-bold leading-none tracking-tight text-white text-[0.84375rem] sm:text-[0.9375rem]';

const lowerStatCaptureStackClass =
  'flex flex-col items-center justify-center gap-0 leading-none [&>span]:block [&>span]:leading-none';

const lowerStatLabelClass =
  'm-0 shrink-0 whitespace-nowrap text-center text-[9px] font-semibold uppercase leading-none tracking-[0.12em] text-white/65 sm:text-[10px] sm:tracking-[0.14em]';

function LowerStatValue({ value }: { value: string }) {
  if (value === 'Photo + Video') {
    return (
      <div className={lowerStatCaptureStackClass}>
        <span className={lowerStatCaptureLineClass}>Photo +</span>
        <span className={lowerStatCaptureLineClass}>Video</span>
      </div>
    );
  }
  if (value === 'Photo') {
    return <span className={lowerStatCaptureSingleClass}>{value}</span>;
  }
  return <span className={lowerStatNumericClass}>{value}</span>;
}

function LowerCategoryStatBox({ proof }: { proof: BlockProof }) {
  const values: [string, string, string] = [proof.tasksValue, proof.subValue, proof.captureValue];
  const labels: [string, string, string] = [
    'Tasks',
    proof.middleColumnLabel === 'languages' ? 'Languages' : 'Subcategories',
    'Capture',
  ];

  return (
    <div className={homepageCategoryStatBoxClassName}>
      <div className={lowerStatBarMobileClass}>
        {([0, 1, 2] as const).map((i) => (
          <div key={i} className={lowerStatMobileSegmentClass}>
            <LowerStatValue value={values[i]} />
            <span className={lowerStatLabelClass}>{labels[i]}</span>
          </div>
        ))}
      </div>
      <div className={lowerStatBarDesktopClass}>
        {([0, 1, 2] as const).map((i) => (
          <div key={`v-${i}`} className={lowerStatDesktopValueCellClass}>
            <LowerStatValue value={values[i]} />
          </div>
        ))}
        {([0, 1, 2] as const).map((i) => (
          <div key={`l-${i}`} className={lowerStatDesktopLabelCellClass}>
            <span className={lowerStatLabelClass}>{labels[i]}</span>
          </div>
        ))}
      </div>
    </div>
  );
}

const RealWorldDataSection: React.FC = () => {
  return (
    <section
      id="training-data"
      className={`border-t border-[rgba(255,255,255,0.05)] bg-[#0B0F14] ${homepageSectionPaddingYClassName}`}
    >
      <div className={homepageSectionContainerClassName}>
        <header className="mx-auto mb-12 lg:mb-14">
          <div className="mx-auto max-w-2xl text-center">
            <h2
              className={`mb-3 inline-block text-3xl font-bold leading-tight tracking-tight text-white sm:mb-4 sm:text-4xl lg:text-[2.5rem] ${sectionHeadingAccentCenterClassName}`}
            >
              Real-World Data, Structured for AI
            </h2>
            <p className="mb-8 text-[15px] leading-relaxed text-white/70 sm:mb-9 sm:text-base">
              Ground truth from live environments—validated, structured, and delivered in the formats your stack already uses.
            </p>
          </div>

          <div
            className={`${homepageElevatedPanelClassName} mx-auto mb-7 w-full max-w-[min(100%,96rem)] overflow-x-auto px-5 py-7 sm:mb-8 sm:px-10 sm:py-8`}
          >
            <div className="mx-auto flex w-full max-w-[min(100%,96rem)] items-center justify-center">
              <div className={topStatGridClass}>
                {STAT_COLUMNS.map((col) => (
                  <TopStatCell key={col.value} column={col} />
                ))}
              </div>
            </div>
          </div>

          <p className="mx-auto max-w-xl text-center text-sm leading-relaxed text-white/60 sm:text-[15px]">
            End-to-end rigor from capture through export—plus custom campaigns when ready-made coverage isn&apos;t enough.
          </p>
        </header>

        <div className="flex flex-col gap-14 lg:gap-[4.25rem]">
          {BLOCKS.map((block) => {
            const rowAlign = block.imageSrc ? 'lg:items-center' : 'lg:items-stretch';
            const rowClasses =
              block.layout === 'image-left'
                ? `flex flex-col gap-9 lg:flex-row ${rowAlign} lg:gap-12 xl:gap-16`
                : `flex flex-col gap-9 lg:flex-row-reverse ${rowAlign} lg:gap-12 xl:gap-16`;

            return (
              <div key={block.title} className={rowClasses}>
                <div className="flex min-h-0 w-full min-w-0 flex-col self-stretch lg:flex-1 lg:basis-0">
                  <HomepageImageGlowWrap
                    className={block.imageSrc ? 'w-full max-w-full' : 'min-h-[420px] flex-1'}
                  >
                    {block.imageSrc ? (
                      <img
                        src={block.imageSrc}
                        alt={block.imageAlt ?? ''}
                        className={realWorldBlockImageClassName}
                        decoding="async"
                      />
                    ) : (
                      <div className={homepageSectionImagePlaceholderStretchClassName} aria-hidden />
                    )}
                  </HomepageImageGlowWrap>
                </div>
                <div className="flex w-full min-w-0 max-w-xl flex-col justify-center py-1 lg:max-w-[36rem] lg:flex-1 lg:basis-0 lg:py-2 xl:pr-4">
                  <h3
                    className={`mb-3.5 inline-block text-2xl font-bold tracking-tight text-white sm:mb-4 sm:text-3xl ${sectionHeadingAccentClassName}`}
                  >
                    {block.title}
                  </h3>
                  <p className="mb-4 text-lg font-semibold leading-snug text-white sm:mb-5 sm:text-xl">
                    {block.lead}
                  </p>
                  <ul className="mb-5 space-y-2.5 sm:space-y-3">
                    {block.bullets.map((item) => (
                      <li key={item} className="text-[15px] leading-[1.55] text-white sm:text-base">
                        {item}
                      </li>
                    ))}
                  </ul>
                  <p className="mb-2 text-[11px] font-semibold uppercase tracking-[1.5px] text-white/45 sm:text-[12px]">
                    Designed to improve
                  </p>
                  <ul className="mb-5 space-y-2">
                    {block.designed.map((item) => (
                      <li key={item} className="flex gap-2.5 text-[15px] text-white sm:text-base">
                        <span className="shrink-0 font-light text-[#00e3ff]/55" aria-hidden>
                          ·
                        </span>
                        <span>{item}</span>
                      </li>
                    ))}
                  </ul>

                  <LowerCategoryStatBox proof={block.proof} />

                  <a href="/request-dataset" className={`mt-3 ${homepageDatasetRequestLinkClassName}`}>
                    <span aria-hidden>→</span> Request dataset for this category
                  </a>
                </div>
              </div>
            );
          })}
        </div>
      </div>
    </section>
  );
};

export default RealWorldDataSection;
